import type { TFunction } from "i18next";

export type TermTemplateConfig = {
  token_format: "BRACE_UPPER";
  token_catalog_version: number;
};

export type TermTokenCategory = "animal" | "guardian" | "clinic" | "general" | "users" | "alerts" | "signature";

export type TermTokenDefinition = {
  key: string;
  label: string;
  category: TermTokenCategory;
};

export const DEFAULT_TERM_TEMPLATE_CONFIG: TermTemplateConfig = {
  token_format: "BRACE_UPPER",
  token_catalog_version: 1,
};

export const getTermTokenDefinitions = (t: TFunction<"terms">): TermTokenDefinition[] => [
  { key: "ANIMAL_NOME", label: t("tokenDefinitions.animalName"), category: "animal" },
  { key: "ANIMAL_FICHA", label: t("tokenDefinitions.animalRecord"), category: "animal" },
  { key: "ANIMAL_SEXO", label: t("tokenDefinitions.animalSex"), category: "animal" },
  { key: "ANIMAL_NASCIMENTO", label: t("tokenDefinitions.animalBirthDate"), category: "animal" },
  { key: "ANIMAL_RACA", label: t("tokenDefinitions.animalBreed"), category: "animal" },
  { key: "ANIMAL_ESPECIE", label: t("tokenDefinitions.animalSpecies"), category: "animal" },
  { key: "ANIMAL_PESO", label: t("tokenDefinitions.animalWeight"), category: "animal" },
  { key: "RESPONSAVEL_NOME", label: t("tokenDefinitions.guardianName"), category: "guardian" },
  { key: "RESPONSAVEL_DOCUMENTO", label: t("tokenDefinitions.guardianDocument"), category: "guardian" },
  { key: "RESPONSAVEL_TELEFONE", label: t("tokenDefinitions.guardianPhone"), category: "guardian" },
  { key: "RESPONSAVEL_EMAIL", label: t("tokenDefinitions.guardianEmail"), category: "guardian" },
  { key: "RESPONSAVEL_ENDERECO", label: t("tokenDefinitions.guardianAddress"), category: "guardian" },
  { key: "CLINICA_NOME", label: t("tokenDefinitions.clinicName"), category: "clinic" },
  { key: "CLINICA_DOCUMENTO", label: t("tokenDefinitions.clinicDocument"), category: "clinic" },
  { key: "CLINICA_TELEFONE", label: t("tokenDefinitions.clinicPhone"), category: "clinic" },
  { key: "CLINICA_ENDERECO", label: t("tokenDefinitions.clinicAddress"), category: "clinic" },
  { key: "DATA_ATUAL", label: t("tokenDefinitions.currentDate"), category: "general" },
  { key: "DATA_HORA_ATUAL", label: t("tokenDefinitions.currentDateTime"), category: "general" },
  { key: "USUARIO_NOME", label: t("tokenDefinitions.userName"), category: "users" },
  { key: "USUARIO_CRMV", label: t("tokenDefinitions.userCrmv"), category: "users" },
  { key: "USUARIO_DOCUMENTO", label: t("tokenDefinitions.userDocument"), category: "users" },
  { key: "ALERTA_GERAL", label: t("tokenDefinitions.generalAlert"), category: "alerts" },
  { key: "ALERTA_VACINAS", label: t("tokenDefinitions.vaccineAlert"), category: "alerts" },
  { key: "ASSINATURA_RESPONSAVEL", label: t("tokenDefinitions.signatureGuardian"), category: "signature" },
  { key: "ASSINATURA_VETERINARIO", label: t("tokenDefinitions.signatureVet"), category: "signature" },
];

export const getTermTokensByCategory = (t: TFunction<"terms">): Record<TermTokenCategory, TermTokenDefinition[]> => {
  const definitions = getTermTokenDefinitions(t);
  return {
    animal: definitions.filter((item) => item.category === "animal"),
    guardian: definitions.filter((item) => item.category === "guardian"),
    clinic: definitions.filter((item) => item.category === "clinic"),
    general: definitions.filter((item) => item.category === "general"),
    users: definitions.filter((item) => item.category === "users"),
    alerts: definitions.filter((item) => item.category === "alerts"),
    signature: definitions.filter((item) => item.category === "signature"),
  };
};

export const normalizeTermTemplateConfig = (value: unknown): TermTemplateConfig => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return DEFAULT_TERM_TEMPLATE_CONFIG;
  return {
    token_format: "BRACE_UPPER",
    token_catalog_version:
      typeof (value as Record<string, unknown>).token_catalog_version === "number"
        ? ((value as Record<string, unknown>).token_catalog_version as number)
        : DEFAULT_TERM_TEMPLATE_CONFIG.token_catalog_version,
  };
};

export const buildTokenLabelMap = (t: TFunction<"terms">) => {
  const map = new Map<string, string>();
  getTermTokenDefinitions(t).forEach((item) => map.set(item.key, item.label));
  return map;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const RAW_HTML_TOKENS = new Set(["ASSINATURA_RESPONSAVEL", "ASSINATURA_VETERINARIO"]);

export const renderTermWithTokenValues = (input: string, values: Record<string, string>) =>
  input.replace(/\{([A-Z0-9_]+)\}/g, (_, token: string) => {
    const value = values[token] ?? "";
    if (RAW_HTML_TOKENS.has(token)) return value;
    return escapeHtml(value).replace(/\n/g, "<br />");
  });

export const getDefaultTermTemplateContent = (t: TFunction<"terms">) => `
<h1 style="text-align: center;">TERMO DE EXEMPLO</h1>
<br/>
<br/>
<p style="text-align: center;">${t("defaultTemplate.paragraph1")}</p>
<p style="text-align: center;">${t("defaultTemplate.paragraph2")}</p>
<br/>
<br/>
<br/>
<br/>
<br/>
<br/>
<div style="text-align: center;">{ASSINATURA_RESPONSAVEL}</div>
`.trim();

export type SignatureTokenInput = {
  vetName: string;
  vetCrmv?: string;
};

/**
 * Returns HTML strings for both signature tokens: the guardian signature line
 * and the vet signature line with name/CRMV. In the digital-signing flow the
 * vet token is overridden to an empty string, since the sign-pdf service
 * stamps the vet info at the bottom of the page.
 */
export const buildSignatureTokenValues = (
  input: SignatureTokenInput,
): Record<string, string> => {
  const physicalBlock = `
<div style="margin-top: 2rem; text-align: center;">
  <p style="border-top: 1px solid #000; display: inline-block; min-width: 260px; padding-top: 4px; margin-bottom: 0;">
    Assinatura do(a) Responsável(a)
  </p>
</div>`.trim();

  const vetLines = [
    input.vetName ? `<p style="margin: 4px 0 0; font-weight: bold;">${escapeHtml(input.vetName)}</p>` : "",
    input.vetCrmv ? `<p style="margin: 2px 0 0; font-size: 0.8rem;">${escapeHtml(input.vetCrmv)}</p>` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const vetBlock = `
<div style="margin-top: 2rem; text-align: center;">
  <p style="border-top: 1px solid #000; display: inline-block; min-width: 260px; padding-top: 4px; margin-bottom: 0;">
    Médico(a) Veterinário(a) Responsável
  </p>
  ${vetLines}
</div>`.trim();

  return {
    ASSINATURA_RESPONSAVEL: physicalBlock,
    ASSINATURA_VETERINARIO: vetBlock,
  };
};
